import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocketContext } from '@/contexts/socket-context'
import { TASK_INVALIDATION_MAP, TASK_PROGRESS_INVALIDATION_MAP } from '@/api/activity-config'
import type { TaskEvent } from '@/hooks/use-socket'

/**
 * Listens for task:completed events and immediately invalidates related
 * React Query caches, giving near-instant data refresh after background
 * operations finish. AI analysis tasks also refresh when they queue, start, or fail.
 */
export function useActivityInvalidation() {
  const { on } = useSocketContext()
  const queryClient = useQueryClient()

  useEffect(() => {
    const handler = (data: TaskEvent) => {
      const keys = TASK_INVALIDATION_MAP[data.name]
      if (!keys) return
      for (const queryKey of keys) {
        queryClient.invalidateQueries({ queryKey })
      }
    }

    const progressHandler = (data: TaskEvent) => {
      for (const queryKey of TASK_PROGRESS_INVALIDATION_MAP[data.name] ?? []) {
        queryClient.invalidateQueries({ queryKey })
      }
    }

    const unsubs = [
      on('task:completed', handler),
      on('task:queued', progressHandler),
      on('task:started', progressHandler),
      on('task:failed', progressHandler),
    ]
    return () => unsubs.forEach((unsub) => unsub())
  }, [on, queryClient])
}
